import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { InvoiceDigestStatus } from 'anchor-scout-common/types';
import { Divider, TableList, Typography } from 'seagull';
import { routes } from '~/constants';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { invoiceStatusToColor } from './InvoicesDigestWidget';
import styles from './InvoicesDigestWidget.module.scss';
import { invoicePaymentStatusMap, getDigestStatusV2Link } from './InvoicesDigestWIdget.utils';
const renderCell = (param)=>{
    let { value, testId } = param;
    return {
        value: /*#__PURE__*/ _jsx(Typography, {
            testId: testId,
            children: value
        })
    };
};
const renderDivider = ()=>/*#__PURE__*/ _jsx(Divider, {
        margin: 0
    });
const renderHeaderItem = (value)=>({
        value: /*#__PURE__*/ _jsx(Typography, {
            weight: "normal",
            children: value
        })
    });
const invoicesDigestTableTextsPath = 'pages.dashboard.invoicesDigestWidget';
const invoiceTypeToTypeCellText = (type, texts, invoiceStatusesV2)=>{
    switch(type){
        case InvoiceDigestStatus.RequiresClientApproval:
            return invoiceStatusesV2 ? texts.statusLabels.clientApprovalRequired() : texts.statusLabels.requiresClientApproval();
        case InvoiceDigestStatus.PaymentsVerificationRequired:
            return texts.statusLabels.paymentsVerificationRequired();
        case InvoiceDigestStatus.MissingPaymentMethod:
            return texts.statusLabels.missingPaymentMethod();
        case InvoiceDigestStatus.Failed:
            return texts.statusLabels.failed();
        case InvoiceDigestStatus.Disputed:
            return texts.statusLabels.disputed();
        case InvoiceDigestStatus.PaymentStopped:
            return texts.statusLabels.paymentStopped();
        case InvoiceDigestStatus.PaymentMethodUnverified:
            return texts.statusLabels.paymentMethodUnverified();
    }
};
function InvoiceInfoRow(param) {
    let { invoiceStatusGroup, invoiceType, testId, className, isLast } = param;
    const { t } = useLocaleKeys(invoicesDigestTableTextsPath);
    const { INVOICE_STATUSES_V2 } = useFeatureFlags();
    const linkTo = INVOICE_STATUSES_V2 ? getDigestStatusV2Link(routes.invoicesDashboard, invoiceType) : "".concat(routes.invoicesDashboard, "?paymentStatus=").concat(invoicePaymentStatusMap[invoiceType]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(TableList.Row, {
                disableMarginBottom: true,
                highlightOnHover: true,
                as: NavLink,
                size: "fluid",
                className: className,
                to: linkTo,
                testId: testId,
                items: [
                    {
                        value: /*#__PURE__*/ _jsxs("div", {
                            className: cn(styles.dotContainer),
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: styles.dot,
                                    style: {
                                        background: invoiceStatusToColor[invoiceType]
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    children: invoiceTypeToTypeCellText(invoiceType, t, !!INVOICE_STATUSES_V2)
                                })
                            ]
                        })
                    },
                    renderCell({
                        value: invoiceStatusGroup.count.toString()
                    }),
                    renderCell({
                        value: invoiceStatusGroup.amount.display()
                    })
                ]
            }),
            !isLast && renderDivider()
        ]
    });
}
function InvoicesInfoTable(param) {
    let { invoiceDigestModel } = param;
    const { t } = useLocaleKeys(invoicesDigestTableTextsPath);
    const invoicesInfoRowTestId = (type)=>"invoices-digest-".concat(type, "-row");
    const rows = invoiceDigestModel.invoiceTypesToDisplay();
    const className = invoiceDigestModel.numInvoiceTypes() < 6 ? styles.lessThanSixItemsInvoiceInfoRow : styles.sixItemsInvoiceInfoRow;
    const { columnDefs, headerItems } = (()=>{
        const config = [
            {
                columnDef: {
                    width: [
                        130,
                        260
                    ]
                },
                headerItem: renderHeaderItem(t.tableHeaders.type())
            },
            {
                columnDef: {
                    width: 60,
                    hAlign: 'end'
                },
                headerItem: renderHeaderItem(t.tableHeaders.invoices())
            },
            {
                columnDef: {
                    width: 80,
                    hAlign: 'end'
                },
                headerItem: renderHeaderItem(t.tableHeaders.amount())
            }
        ];
        return {
            columnDefs: config.map((i)=>i.columnDef),
            headerItems: config.map((i)=>i.headerItem)
        };
    })();
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": "invoice-digest-table",
        children: /*#__PURE__*/ _jsxs(TableList.Provider, {
            disableMobile: true,
            variant: "minimal",
            columnDefs: columnDefs,
            hPadding: "xs",
            children: [
                /*#__PURE__*/ _jsx(TableList.Header, {
                    items: headerItems,
                    bgColor: "transparent"
                }),
                renderDivider(),
                rows.map((invoiceType, idx)=>{
                    const invoiceStatusGroup = invoiceDigestModel.invoiceTypeToInvoicesGroup(invoiceType);
                    return /*#__PURE__*/ _jsx(InvoiceInfoRow, {
                        testId: invoicesInfoRowTestId(invoiceType),
                        invoiceType: invoiceType,
                        invoiceStatusGroup: invoiceStatusGroup,
                        className: className,
                        isLast: idx === rows.length - 1
                    }, invoiceType);
                })
            ]
        })
    });
}
export default InvoicesInfoTable;
